/**
 * Hook for processing ViaMichelin URLs with tracking parameters
 *
 * This hook automatically applies tracking parameters to ViaMichelin URLs
 * using the buildViaMichelinUrl function.
 */

import {useMemo} from "react";
import {buildViaMichelinUrl, isViaMichelinUrl} from "@/utils/viaMichelinUrlBuilder";

/**
 * Hook that processes a URL and adds tracking parameters if it's a ViaMichelin URL
 *
 * @param url - The URL to process
 * @param locale - Optional locale for context
 * @returns The processed URL with tracking parameters if it's a ViaMichelin URL, otherwise the original URL
 *
 * @example
 * ```typescript
 * const trackedUrl = useViaMichelinUrl('https://www.viamichelin.fr/route', 'fr');
 * // Returns: 'https://www.viamichelin.fr/route?origine=magvm&utm_source=magviamichelin'
 * ```
 */
export function useViaMichelinUrl(url?: string, locale?: string): string {
  return useMemo(() => {
    if (!url || typeof url !== "string") {
      return url || "";
    }

    // Check if this is a ViaMichelin URL
    if (isViaMichelinUrl(url)) {
      try {
        return buildViaMichelinUrl({baseUrl: url, locale});
      } catch {
        // If tracking fails, return original URL
        return url;
      }
    }

    return url;
  }, [url, locale]);
}
